import z from 'zod';
import { AllCharLabelSchema, UUIDSchema, UnixTimestampSchema } from '../common/schemas';
import { DBNumericSchema, DBTimestampSchema } from '../common/sql';
import { ScaleKind } from './enums';

// String bounds mirror the tables' own CHECK constraints; a tighter one would
// warn on rows the database accepts.

export const DBVocabularyTermCodeSchema = z
  .string()
  .regex(/^[a-z0-9]+(_[a-z0-9]+)*$/, 'A code is lowercase words joined by underscores');

export const DBVocabularyTermLabelSchema = AllCharLabelSchema;
export const DBVocabularyTermDescriptionSchema = AllCharLabelSchema.nullable();

const dbTermColumns = {
  id: UUIDSchema,
  code: DBVocabularyTermCodeSchema,
  label: DBVocabularyTermLabelSchema,
  description: DBVocabularyTermDescriptionSchema,
  isActive: z.boolean(),
  sortOrder: z.number().int(),
  createdAt: DBTimestampSchema,
  updatedAt: DBTimestampSchema.nullable(),
};

export const DBVocabularyTermSchema = z.object(dbTermColumns);

/** The one vocabulary carrying a range, which every score on it is held to. */
export const DBScaleSchema = z.object({
  ...dbTermColumns,
  scaleKind: z.enum(ScaleKind),
  minValue: DBNumericSchema,
  maxValue: DBNumericSchema,
});

/** What a number on a scale means, so a display never hard-codes that 3 is "Apply". */
export const DBScalePointSchema = z.object({
  id: UUIDSchema,
  scaleId: UUIDSchema,
  value: DBNumericSchema,
  code: DBVocabularyTermCodeSchema,
  label: AllCharLabelSchema.min(1),
  description: AllCharLabelSchema.nullable(),
  sortOrder: z.number().int(),
  createdAt: DBTimestampSchema,
  updatedAt: DBTimestampSchema.nullable(),
});

/**
 * Client Schemas
 */

export const VocabularyTermCodeSchema = DBVocabularyTermCodeSchema.openapi({
  description: 'Stable identifier for a vocabulary value',
  example: 'multiple_choice',
});

export const VocabularyTermLabelSchema = DBVocabularyTermLabelSchema.openapi({
  description: 'How the value reads on screen',
  example: 'Multiple choice',
});

const termFields = {
  id: UUIDSchema,
  code: VocabularyTermCodeSchema,
  label: VocabularyTermLabelSchema,
  description: DBVocabularyTermDescriptionSchema,
  isActive: z.boolean(),
  sortOrder: z.number().int(),
  createdAt: UnixTimestampSchema,
  updatedAt: UnixTimestampSchema.nullable(),
};

export const VocabularyTermSchema = z.object(termFields);

export const ScaleKindSchema = z.enum(ScaleKind).openapi({
  description: 'Whether an axis is scored in ranks, whole counts, or a continuous range',
  example: ScaleKind.Ordinal,
});

export const ScaleSchema = z.object({
  ...termFields,
  scaleKind: ScaleKindSchema,
  minValue: z.number(),
  maxValue: z.number(),
});

export const ScalePointSchema = z.object({
  id: UUIDSchema,
  scaleId: UUIDSchema,
  value: z.number(),
  code: VocabularyTermCodeSchema,
  label: AllCharLabelSchema.min(1),
  description: AllCharLabelSchema.nullable(),
  sortOrder: z.number().int(),
  createdAt: UnixTimestampSchema,
  updatedAt: UnixTimestampSchema.nullable(),
});
